import React, { useEffect, useMemo } from 'react';
import { Modal } from '@aibuild/ui';
import type { ProcessNode } from './graphTypes';
import RunConfigForm, {
  type RunConfigFormValue,
  type RunConfigFormInputError,
} from './run/RunConfigForm';
import { liveInputNodeToDto } from './run/liveInputNodeToDto';
import { Body } from './TestRunDialog.styled';

export interface BaseRunDialogProps {
  open: boolean;
  onClose: () => void;
  nodes: readonly ProcessNode[];
  isStarting: boolean;
  inputError?: RunConfigFormInputError | null;
  onStart: (values: Readonly<Record<string, RunConfigFormValue>>) => Promise<void>;
  onClearError?: () => void;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  submitLabel: string;
  dataTestId?: string;
}

const BaseRunDialog: React.FC<BaseRunDialogProps> = ({
  open,
  onClose,
  nodes,
  isStarting,
  inputError,
  onStart,
  onClearError,
  title,
  subtitle,
  icon,
  submitLabel,
  dataTestId = 'run-dialog',
}) => {
  const inputNodeDtos = useMemo(
    () =>
      nodes
        .filter((n) => {
          if (n.type !== 'input') return false;
          // Guard the read because n.data is a discriminated-union and a
          // malformed/loading node can legitimately have no dataType yet.
          // We keep nodes whose dataType is anything other than the
          // string literal 'NONE' (including missing/typo'd values) to
          // preserve the original intent of "show every node that needs a
          // value collected".
          const data: { dataType?: unknown } | undefined = n.data as
            | { dataType?: unknown }
            | undefined;
          const dataType = data?.dataType;
          return typeof dataType !== 'string' || dataType !== 'NONE';
        })
        .map(liveInputNodeToDto),
    [nodes],
  );

  useEffect(() => {
    if (open) onClearError?.();
  }, [open, onClearError]);

  const handleSubmit = async (values: Record<string, RunConfigFormValue>) => {
    try {
      await onStart(values);
    } catch {
      // The parent owns the user-facing error state (see `inputError`
      // and `onClearError`) and already surfaces failures via that
      // channel. We swallow here purely to stop the rejection from
      // escaping RunConfigForm's onSubmit, which doesn't await us and
      // would otherwise produce an unhandled-promise-rejection.
    }
  };

  return (
    <Modal
      open={open}
      onClose={isStarting ? () => undefined : onClose}
      title={title}
      subtitle={subtitle}
      icon={icon}
      size="md"
      dataTestId={dataTestId}
      hideCloseButton={isStarting}
      disableEscape={isStarting}
      disableBackdropClose={isStarting}
    >
      <Body data-testid={`${dataTestId}__body`}>
        <RunConfigForm
          inputNodes={inputNodeDtos}
          onSubmit={handleSubmit}
          onCancel={onClose}
          isSubmitting={isStarting}
          inputError={inputError ?? null}
          submitLabel={submitLabel}
          dataTestId={`${dataTestId}__form`}
        />
      </Body>
    </Modal>
  );
};

export default BaseRunDialog;
